// src/screens/MarketplaceDetalleScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { doc, getDoc, collection, query, where, onSnapshot } from 'firebase/firestore';
import {
  IoArrowBack,
  IoStar,
  IoStarHalf,
  IoStarOutline,
  IoTimeOutline,
  IoWalletOutline,
  IoPersonCircleOutline,
  IoRibbonOutline,
  IoPaperPlaneOutline,
  IoDiamond,
  IoRibbon
} from 'react-icons/io5';
import * as Ionicons from 'react-icons/io5';
import moment from 'moment';
import 'moment/locale/es';
import './MarketplaceDetalleScreen.css';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';

moment.locale('es');

// Sub-componentes
const StarRating = ({ rating }) => {
  const totalStars = 5;
  let stars = [];
  for (let i = 1; i <= totalStars; i++) {
    if (i <= rating) {
      stars.push(<IoStar key={i} />);
    } else if (i - 0.5 <= rating) {
      stars.push(<IoStarHalf key={i} />);
    } else {
      stars.push(<IoStarOutline key={i} />);
    }
  }
  return <div className="detail-star-box">{stars}</div>;
};

const PriceRange = ({ range }) => {
  const rangeStr = range && typeof range === 'string' ? range : '$';
  return (
    <div className="detail-price-row">
      <span className={rangeStr.length >= 1 ? 'active' : ''}>$</span>
      <span className={rangeStr.length >= 2 ? 'active' : ''}>$</span>
      <span className={rangeStr.length >= 3 ? 'active' : ''}>$</span>
    </div>
  );
};

function MarketplaceDetalleScreen() {
  const navigate = useNavigate();
  const { proveedorId } = useParams();

  const [proveedor, setProveedor] = useState(null);
  const [resenas, setResenas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [categoryColor, setCategoryColor] = useState('#6348D1'); // fallback purple
  const [categoryIcon, setCategoryIcon] = useState('IoStorefrontOutline');

  useEffect(() => {
    if (!proveedorId) {
      setError("No se especificó un proveedor.");
      setLoading(false);
      return;
    }

    setLoading(true);
    const fetchProveedor = async () => {
      try {
        const docRef = doc(db, "market_proveedores", proveedorId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setProveedor({ id: docSnap.id, ...docSnap.data() });
        } else {
          setError("Proveedor no encontrado.");
        }
      } catch (e) {
        console.error("Error al cargar proveedor: ", e);
        setError("Error al cargar los datos del proveedor.");
      }
      setLoading(false);
    };

    fetchProveedor();

    const qResenas = query(
      collection(db, "market_resenas"),
      where("proveedorId", "==", proveedorId)
    );

    const unsubscribe = onSnapshot(qResenas, (snapshot) => {
      const listaResenas = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Ordenar por fecha descendente en cliente
      listaResenas.sort((a, b) => {
        const dateA = a.fecha?.toDate ? a.fecha.toDate() : new Date(0);
        const dateB = b.fecha?.toDate ? b.fecha.toDate() : new Date(0);
        return dateB - dateA;
      });
      setResenas(listaResenas);
    });

    return () => unsubscribe();
  }, [proveedorId]);

  useEffect(() => {
    if (!proveedor?.categoria) return;

    const q = query(collection(db, 'marketplace_categorias'), where('activo', '==', true));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.forEach(docSnap => {
        const cat = docSnap.data();
        if (cat.nombre.toLowerCase() === proveedor.categoria.toLowerCase()) {
          if (cat.color) setCategoryColor(cat.color);
          if (cat.iconoNombre) setCategoryIcon(cat.iconoNombre);
        }
      });
    }, (error) => {
      console.error("Error loading category metadata for details:", error);
    });

    return () => unsubscribe();
  }, [proveedor]);

  const getTopTags = () => {
    if (!proveedor || !proveedor.tagsCounts) return [];
    const entries = Object.entries(proveedor.tagsCounts);
    entries.sort((a, b) => b[1] - a[1]);
    return entries.slice(0, 3).map(entry => entry[0]);
  };

  const renderHeroIcon = (iconName) => {
    const IconComponent = Ionicons[iconName] || Ionicons['IoStorefrontOutline'];
    return <IconComponent className="marketplace-detail-hero-icon-decor" />;
  };

  const renderSmallIcon = (iconName) => {
    const IconComponent = Ionicons[iconName] || Ionicons['IoStorefrontOutline'];
    return <IconComponent size={24} color="white" style={{ opacity: 0.95 }} />;
  };

  if (loading) return <div className="detail-container"><p>Cargando perfil...</p></div>;
  if (error) return (
    <div className="detail-container">
      <div className="detail-header-nav">
        <button onClick={() => navigate(-1)} className="detail-back-btn"><IoArrowBack /></button>
        <h1 className="detail-title">Error</h1>
      </div>
      <p style={{ color: 'red' }}>{error}</p>
    </div>
  );
  if (!proveedor) return null;
  const topTags = getTopTags();

  return (
    <div className="ks-page">
      {/* ── DESKTOP SIDEBAR ─────────────────────── */}
      <KondoSidebar activeRoute="/marketplace" />

      {/* ── MAIN AREA ────────────────────────────── */}
      <main className="ks-main">
        <div className="ks-scroll-area">
          <div className="ks-marketplace-header-sticky-wrapper">
            <KondoBrandHeader showBack={false} />
          </div>

          <div className="marketplace-detail-content-wrapper">
            {/* Premium Scoped Hero Banner */}
            <div className="marketplace-header-stitch">
              <div
                className="marketplace-detail-hero-card"
                style={
                  categoryColor
                    ? { background: `linear-gradient(135deg, ${categoryColor}dd 0%, ${categoryColor} 100%)` }
                    : { background: 'linear-gradient(135deg, #7B61FF 0%, #9B8CFF 100%)' }
                }
              >
                <div className="marketplace-detail-hero-content">
                  <button onClick={() => navigate(-1)} className="marketplace-detail-glass-back-btn" title="Atrás">
                    <IoArrowBack size={20} color="white" />
                  </button>
                  <div className="marketplace-detail-hero-text-col">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {categoryIcon && renderSmallIcon(categoryIcon)}
                      <h1>Perfil del Proveedor</h1>
                    </div>
                    <p>
                      Conoce más sobre la experiencia, tarifas y opiniones del especialista.
                    </p>
                  </div>
                </div>
                {categoryIcon && renderHeroIcon(categoryIcon)}
              </div>
            </div>

            <div className="detail-content-scroll">
              {/* LÓGICA DE TIERS B2B P/ INSIGNIAS */}
              {(() => {
                const planId = proveedor.marketPlanId || 'tier_plata';
                const isSocio = planId.toLowerCase().includes('socio');
                const isDiamante = planId === 'tier_diamante';
                const isOro = planId === 'tier_oro';
                const isPremium = isDiamante || isOro || isSocio;

                const badgeText = isSocio ? 'SOCIO EXCLUSIVO' : 'PATROCINADO';
                const verifiedIconColor = isSocio ? '#fbbf24' : (isDiamante ? '#a855f7' : '#eab308'); // Oro intenso para Socio, Lila para Diamante
                const badgeBg = isSocio ? 'linear-gradient(135deg, #1f2937, #000000)' : (isDiamante ? 'linear-gradient(135deg, #a855f7, #9333ea)' : '#eab308');

                return (
                  <div className={`detail-hero-card ${isPremium ? 'premium ' + (isSocio ? 'socio' : (isDiamante ? 'diamante' : 'oro')) : ''}`}>
                    {/* Premium Banner/Badge */}
                    {isPremium && (
                      <div style={{
                        position: 'absolute', top: 0, left: '50%', transform: 'translate(-50%, -50%)',
                        background: badgeBg, color: 'white', padding: '4px 12px',
                        borderRadius: '12px', fontSize: '0.75rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)', zIndex: 10
                      }}>
                        <span className="badge-text">{badgeText}</span>
                      </div>
                    )}

                    <div className="detail-logo-box" style={isPremium ? { border: `3px solid ${verifiedIconColor}`, padding: '2px', position: 'relative' } : { position: 'relative' }}>
                      <img
                        src={proveedor.logoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(proveedor.nombrePublico)}&background=random&size=200`}
                        alt={proveedor.nombrePublico}
                      />
                      {/* SELLO PREMIUM FLOTANTE */}
                      {isPremium && (
                        <div className={`premium-stamp ${isSocio ? 'socio' : (isDiamante ? 'diamante' : 'oro')}`}>
                          {isSocio ? <IoRibbon size={24} /> : (isDiamante ? <IoDiamond size={24} /> : <IoStar size={24} />)}
                        </div>
                      )}
                    </div>
                    <div className="detail-info-box">
                      <h2 className="detail-provider-name premium-name" style={{ display: 'flex', alignItems: 'center', gap: '5px', justifyContent: 'center' }}>
                        {proveedor.nombrePublico}
                        {isSocio && <IoRibbon size={18} style={{ color: verifiedIconColor }} title="Socio Exclusivo" />}
                        {isDiamante && <IoDiamond size={18} style={{ color: verifiedIconColor }} title="Garantía Diamante" />}
                        {isOro && <IoStar size={18} style={{ color: verifiedIconColor }} title="Proveedor Destacado" />}
                      </h2>

                      <div className="detail-rating-row">
                        <StarRating rating={proveedor.ratingPromedio || 0} />
                        <span className="detail-rating-text">({proveedor.totalCalificaciones || 0})</span>
                        <span className="dot-sep">•</span>
                        <PriceRange range={proveedor.rangoPrecios} />
                      </div>

                      {topTags.length > 0 && (
                        <div className="detail-tags-row">
                          {topTags.map(tag => (
                            <span key={tag} className="tag-pill">
                              <IoRibbonOutline /> {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Stats Grid - 2 Column */}
              <div className="detail-stats-grid">
                <div className="stat-card-compact">
                  <div className="stat-icon-compact"><IoTimeOutline /></div>
                  <div className="stat-info-compact">
                    <span className="stat-label-compact">Tiempo Respuesta</span>
                    <span className="stat-value-compact">{proveedor.tiempoRespuestaHoras ? `${proveedor.tiempoRespuestaHoras}h` : 'N/A'}</span>
                  </div>
                </div>
                <div className="stat-card-compact">
                  <div className="stat-icon-compact"><IoWalletOutline /></div>
                  <div className="stat-info-compact">
                    <span className="stat-label-compact">Costo Visita</span>
                    <span className="stat-value-compact">${proveedor.costoVisita || '0'}</span>
                  </div>
                </div>
              </div>

              {/* Descripción Section */}
              <div className="detail-section">
                <h4 className="section-title-small">Sobre este profesional</h4>
                <p className="detail-desc-text">
                  {proveedor.descripcion || "Sin descripción disponible."}
                </p>
              </div>

              {/* Especialidades */}
              {proveedor.especialidades && proveedor.especialidades.length > 0 && (
                <div className="detail-section">
                  <h4 className="section-title-small">Especialidades</h4>
                  <div className="specialties-container">
                    {proveedor.especialidades
                      .flatMap(esp => esp.split(',')) // Split comma-separated strings
                      .map(tag => tag.trim())         // Remove extra spaces
                      .filter(tag => tag.length > 0)  // Remove empty strings
                      .map((tag, i) => (
                        <span key={i} className="specialty-badge">{tag}</span>
                      ))}
                  </div>
                </div>
              )}

              {/* Galería */}
              <div className="detail-section">
                <h4 className="section-title-small">Galería de Trabajos</h4>
                <div className="gallery-grid">
                  {proveedor.fotosGaleria && proveedor.fotosGaleria.length > 0 ? (
                    proveedor.fotosGaleria.map((url, i) => (
                      <img key={i} src={url} alt={`Trabajo ${i + 1}`} className="gallery-img" />
                    ))
                  ) : (
                    <p style={{ color: '#6b7280', gridColumn: '1/-1' }}>Este proveedor aún no ha subido fotos de sus trabajos.</p>
                  )}
                </div>
              </div>

              {/* Reseñas */}
              <div className="detail-section">
                <h4 className="section-title-small">Reseñas y Opiniones</h4>
                <div className="reviews-container">
                  {resenas.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px', backgroundColor: 'white', borderRadius: '16px' }}>
                      <p style={{ color: '#9ca3af' }}>Aún no hay reseñas. ¡Sé el primero en calificar este servicio!</p>
                    </div>
                  ) : (
                    resenas.map(resena => (
                      <div key={resena.id} className="review-card-premium">
                        <div className="review-header">
                          <div className="reviewer-info">
                            <IoPersonCircleOutline size={30} color="#9ca3af" />
                            <div>
                              <div className="reviewer-name">{resena.nombreResidente || 'Residente'}</div>
                              <div className="review-date">{resena.fecha ? moment(resena.fecha.toDate()).fromNow() : ''}</div>
                            </div>
                          </div>
                          <StarRating rating={resena.calificacion} />
                        </div>

                        {resena.tags && resena.tags.length > 0 && (
                          <div className="detail-tags-row" style={{ marginBottom: '10px' }}>
                            {resena.tags.map(tag => (
                              <span key={tag} className="tag-pill" style={{ fontSize: '0.75em', padding: '4px 8px' }}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}

                        <p className="review-text">"{resena.comentario}"</p>

                        {resena.fotosUrls && resena.fotosUrls.length > 0 && (
                          <div className="review-photos">
                            {resena.fotosUrls.map((url, k) => (
                              <img key={k} src={url} alt="Evidencia" className="review-thumb" />
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Bottom Action Bar */}
        <div className="fixed-bottom-action-bar">
          <button
            className="btn-request-quote-sticky"
            onClick={() => navigate(`/marketplace/cotizar/${proveedor.id}`)}
          >
            <span>Solicitar Cotización</span>
            <IoPaperPlaneOutline />
          </button>
        </div>

        {/* ── FLOATING ISLAND BOTTOM NAV (mobile) ── */}
        <KondoBottomNav activeRoute="/marketplace" />
      </main>
    </div>
  );
}

export default MarketplaceDetalleScreen;